/**
 * KNLS Typing Tutor — speed games
 */

import { CURRICULUM } from './typingTutorCurriculum';
import { KEYBOARD_ROWS } from './fingerKeyMap';

export const GAME_TYPES = [
  {
    id: 'speed_blitz',
    title: 'Speed Blitz',
    subtitle: '60-second word sprint',
    description: 'Type as many words as you can before time runs out. Build combos for bonus points.',
    icon: 'bolt',
    color: '#e67e22',
    order: 1,
  },
  {
    id: 'key_pop',
    title: 'Key Pop',
    subtitle: 'Bubble burst drill',
    description: 'Pop letter bubbles before they vanish. Trains finger memory and reaction speed.',
    icon: 'bubble_chart',
    color: '#8e44ad',
    order: 2,
  },
  {
    id: 'type_racer',
    title: 'Type Racer',
    subtitle: 'Race the ghost',
    description: 'Beat a ghost racer moving at your course WPM goal. Finish the passage first to win.',
    icon: 'directions_run',
    color: '#2980b9',
    order: 3,
  },
];

export const BLITZ_DURATIONS = [
  { id: 'blitz-30', seconds: 30, label: '30 sec' },
  { id: 'blitz-60', seconds: 60, label: '60 sec' },
  { id: 'blitz-120', seconds: 120, label: '2 min' },
];

export const KEY_POP_LEVELS = [
  {
    id: 'keypop-home',
    title: 'Home Row',
    keys: ['a', 's', 'd', 'f', 'j', 'k', 'l', ';'],
    duration: 45,
    targetKeys: 40,
  },
  {
    id: 'keypop-top',
    title: 'Home + Top Row',
    keys: ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';', 'q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    duration: 60,
    targetKeys: 50,
  },
  {
    id: 'keypop-full',
    title: 'Full Keyboard',
    keys: KEYBOARD_ROWS.flat().filter((k) => k.length === 1),
    duration: 60,
    targetKeys: 55,
  },
];

const RACER_PASSAGES = [
  'Learning to type quickly is one of the most useful skills for DCA and PGDCA students. Practice every day on the KNLS typing tutor and your speed will grow steadily. Focus on accuracy first, then push for more words per minute.',
  'Microsoft Word Excel and PowerPoint are essential tools in every office. A good typing speed helps you finish assignments faster and perform better in exams. Keep your fingers on the home row and look at the screen not the keyboard.',
  'Ravi joined KNLS with twelve words per minute. After daily practice on fingering and word drills he reached thirty five words per minute in three months. Consistency beats intensity when learning any new skill including typing.',
  'In typing examinations candidates type a passage within a time limit. Words per minute is calculated from correctly typed characters. DCA students aim for thirty five WPM while DTP and PGDCA students aim for fifty five WPM.',
];

/** Flat word pool from curriculum + extras */
export const buildWordPool = () => {
  const words = new Set();
  (CURRICULUM.words || []).forEach((lesson) => {
    lesson.text.split(/\s+/).forEach((w) => {
      const clean = w.toLowerCase().replace(/[^a-z]/g, '');
      if (clean.length >= 2) words.add(clean);
    });
  });
  [
    'type', 'fast', 'speed', 'skill', 'learn', 'practice', 'focus', 'goal', 'knls',
    'student', 'course', 'lesson', 'keyboard', 'finger', 'home', 'row', 'accuracy',
    'computer', 'office', 'excel', 'word', 'data', 'entry', 'exam', 'pass', 'win',
    'quick', 'steady', 'rhythm', 'train', 'build', 'grow', 'master', 'daily', 'work',
  ].forEach((w) => words.add(w));
  return [...words];
};

export const shuffleArray = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

export const buildWordStream = (count = 200) => {
  const pool = buildWordPool();
  const stream = [];
  while (stream.length < count) {
    stream.push(...shuffleArray(pool));
  }
  return stream.slice(0, count);
};

export const pickRacerPassage = () =>
  RACER_PASSAGES[Math.floor(Math.random() * RACER_PASSAGES.length)];

export const getGameType = (gameId) =>
  GAME_TYPES.find((g) => g.id === gameId) || null;

export const getBlitzDuration = (variantId) =>
  BLITZ_DURATIONS.find((d) => d.id === variantId) || BLITZ_DURATIONS[1];

export const getKeyPopLevel = (variantId) =>
  KEY_POP_LEVELS.find((l) => l.id === variantId) || KEY_POP_LEVELS[0];

/** Combo multiplier: streak of 5+ = 1.2x, 10+ = 1.5x, 20+ = 2x */
export const getComboMultiplier = (streak) => {
  if (streak >= 20) return 2;
  if (streak >= 10) return 1.5;
  if (streak >= 5) return 1.2;
  return 1;
};

export const getGameVariant = (gameId, variantId) => {
  if (gameId === 'speed_blitz') return getBlitzDuration(variantId);
  if (gameId === 'key_pop') return getKeyPopLevel(variantId);
  if (gameId === 'type_racer') return { id: 'racer-1', title: 'Race Track' };
  return null;
};
